import {
  Alert,
  Button,
  Dropdown,
  Input,
  Menu,
  message,
  Modal,
  Space,
  Table,
  Tooltip,
} from "antd";
import type {ColumnsType} from "antd/es/table";
import {Link} from "react-router-dom";
import {ExclamationCircleOutlined, MoreOutlined} from "@ant-design/icons";
import {
  StyledDescriptionText,
  StyledNoUpcomingCallBox,
} from "../../../../components/calls/upcoming-calls/upcoming-calls.style";
import {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import useDebounce from "../../../../hooks/use-debounce";
import {useSubscriptionsTableQuery} from "../../../../react-query-hooks/store/subscriptions/subscriptions-table-query";
import type {SubscriptionListItem} from "../../../../react-query-hooks/store/subscriptions/list-subscriptions/types";
import {useGetProfileContext} from "../../../../context/profile/profile-context";
import {handleSubscriptionLink} from "../../../../utils/products";
import {useDeleteSubscriptionApi} from "../../../../react-query-hooks/store/subscriptions/delete-subscription";
import {formatCurrencyIsoCodeAndAmount} from "../../../../utils/format-currency-amount";

const PAGE_SIZE = 5;
const SEARCH_DEBOUNCE_MS = 300;

function subscriptionSlugFromName(name: string) {
  return name.trim().toLowerCase().replace(/\s+/g, "-") || "subscription";
}

function formatErrorMessage(err: unknown) {
  if (err && typeof err === "object" && "message" in err) {
    return String((err as {message: unknown}).message);
  }
  return "Something went wrong while loading subscriptions.";
}

function formatSubscriptionTotalRevenue(
  value: string | null | undefined,
  currencyCode: string | undefined
) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    const code = currencyCode?.trim();
    return code ? `${code} ${value}` : value;
  }
  const code = currencyCode?.trim();
  if (code) {
    return formatCurrencyIsoCodeAndAmount(parsed, code);
  }
  return parsed.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function SubscriptionsEntryPoint({
  listQueryEnabled = true,
}: {
  listQueryEnabled?: boolean;
}) {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);
  const searchForApi = debouncedSearch.trim();
  const {state: profileState} = useGetProfileContext();
  const deleteMutation = useDeleteSubscriptionApi();
  const navigate = useNavigate();

  useEffect(() => {
    setPage(1);
  }, [searchForApi]);

  const tableQuery = useSubscriptionsTableQuery({
    enabled: listQueryEnabled,
    page,
    perPage: PAGE_SIZE,
    searchQuery: searchForApi,
  });

  const subscriptions = tableQuery.data?.subscriptions ?? [];
  const totalCount = tableQuery.data?.total ?? 0;
  const hasPrevPage = !!tableQuery.data?.hasPrevPage;
  const hasNextPage = !!tableQuery.data?.hasNextPage;
  const isUnfiltered = !searchForApi;

  const dataSource = useMemo(
    () =>
      (tableQuery.data?.subscriptions ?? []).map((row) => ({
        ...row,
        key: row.id,
      })),
    [tableQuery.data]
  );

  const clearSearch = () => {
    setSearchQuery("");
  };

  const columns = useMemo<ColumnsType<SubscriptionListItem>>(
    () => [
      {
        title: "Subscription name",
        key: "name",
        fixed: "left" as const,
        width: "180px",
        onCell: () => ({
          style: {
            width: "180px",
            minWidth: "180px",
            maxWidth: "180px",
          },
        }),
        render: (_: unknown, record: SubscriptionListItem) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: "4.4rem",
                height: "2.2rem",
              }}
            >
              <img
                src={record.coverImage || "/images/no-image-placeholder.png"}
                alt={record.name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "2px",
                }}
              />
            </div>
            <Tooltip title={record.name}>
              <StyledDescriptionText>{record.name}</StyledDescriptionText>
            </Tooltip>
          </div>
        ),
      },
      {
        title: "Tiers",
        dataIndex: "totalTiers",
        key: "totalTiers",
        width: 72,
      },
      {
        title: "Active subscribers",
        dataIndex: "activeSubscribers",
        key: "activeSubscribers",
        width: 140,
      },
      {
        title: "Total revenue",
        dataIndex: "totalRevenue",
        key: "totalRevenue",
        width: 120,
        render: (value: string, record: SubscriptionListItem) => {
          const code = record.revenueDisplayCurrency ?? record.defaultCurrency ?? "";
          return <span>{formatSubscriptionTotalRevenue(value, code || undefined)}</span>;
        },
      },
      {
        title: "",
        key: "action",
        width: 56,
        onCell: () => ({
          onClick: (e: React.MouseEvent<HTMLElement>) => e.stopPropagation(),
        }),
        render: (_: unknown, record: SubscriptionListItem) => {
          const username = profileState.profileData?.username;
          const slug = record.slug?.trim()
            ? record.slug
            : subscriptionSlugFromName(record.name);

          const copyLink = () => {
            if (!username) {
              message.error("Could not load your profile. Try again in a moment.");
              return;
            }
            const url = handleSubscriptionLink({
              subscriptionId: record.id,
              slug,
              username,
              tinyUrl: record.tinyUrl?.trim(),
              windowNav: true,
            });
            void navigator.clipboard.writeText(url).then(() => {
              message.success("Link copied to clipboard");
            });
          };

          const menu = (
            <Menu onClick={(info) => info.domEvent.stopPropagation()}>
              <Menu.Item key="insights">
                <Link
                  to={`/products/subscriptions/edit/${record.id}?activeTab=salesAndInsights`}
                >
                  Sales and insights
                </Link>
              </Menu.Item>
              <Menu.Item key="edit">
                <Link to={`/products/subscriptions/edit/${record.id}`}>
                  Edit subscription
                </Link>
              </Menu.Item>
              <Menu.Item key="copy" onClick={copyLink}>
                Copy link
              </Menu.Item>
              <Menu.Item
                key="delete"
                danger
                onClick={() => {
                  Modal.confirm({
                    title: "Confirm subscription deletion",
                    content:
                      "Are you sure you want to delete this subscription? This action cannot be undone.",
                    icon: <ExclamationCircleOutlined style={{color: "#F5222D"}} />,
                    okText: "Delete subscription",
                    cancelText: "Exit",
                    okType: "danger",
                    okButtonProps: {
                      size: "small",
                      type: "primary",
                      loading: deleteMutation.isLoading,
                    },
                    cancelButtonProps: {
                      size: "small",
                      type: "ghost",
                    },
                    onOk: () =>
                      deleteMutation.mutateAsync(record.id).then(() => {
                        message.success(
                          "The selected subscription has been deleted successfully!"
                        );
                      }),
                  });
                }}
              >
                Delete subscription
              </Menu.Item>
            </Menu>
          );

          return (
            <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
              <Button
                shape="circle"
                size="small"
                icon={<MoreOutlined />}
                onClick={(e) => e.stopPropagation()}
                style={{
                  backgroundColor: "transparent",
                  color: "#1C1726",
                  border: "none",
                  boxShadow: "none",
                }}
              />
            </Dropdown>
          );
        },
      },
    ],
    [profileState.profileData?.username, deleteMutation]
  );

  if (!listQueryEnabled) {
    return null;
  }

  if (tableQuery.isError) {
    return <Alert message={formatErrorMessage(tableQuery.error)} type="error" showIcon />;
  }

  if (tableQuery.isLoading && !tableQuery.data) {
    return (
      <Space direction="vertical" size={20} style={{display: "flex", width: "100%"}}>
        <Input
          allowClear
          size="large"
          placeholder="Search subscriptions"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Table
          columns={columns}
          dataSource={[]}
          loading
          pagination={false}
          tableLayout="fixed"
          scroll={{x: "max-content"}}
          onRow={(record: SubscriptionListItem) => ({
            onClick: () =>
              navigate(
                `/products/subscriptions/edit/${record.id}?activeTab=salesAndInsights`
              ),
            style: {cursor: "pointer"},
          })}
        />
      </Space>
    );
  }

  if (totalCount === 0 && isUnfiltered && !tableQuery.isFetching) {
    return (
      <StyledNoUpcomingCallBox>
        <p className="info-text">You do not have any subscriptions at the moment.</p>
        <Link to="/products/subscriptions/new">
          <Button type="primary" size="small">
            Create a new subscription
          </Button>
        </Link>
      </StyledNoUpcomingCallBox>
    );
  }

  return (
    <div>
      <Space direction="vertical" size={20} style={{display: "flex", width: "100%"}}>
        <Input
          allowClear
          size="large"
          placeholder="Search subscriptions"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        {subscriptions.length === 0 && !tableQuery.isFetching ? (
          <StyledNoUpcomingCallBox>
            <p className="info-text">
              {searchForApi
                ? "No subscriptions match your search."
                : "No subscriptions to display."}
            </p>
            {searchForApi ? (
              <Button size="small" type="primary" onClick={clearSearch}>
                Clear search
              </Button>
            ) : null}
          </StyledNoUpcomingCallBox>
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={dataSource}
              pagination={false}
              loading={tableQuery.isFetching}
              tableLayout="fixed"
              scroll={{x: "max-content"}}
              onRow={(record: SubscriptionListItem) => ({
                onClick: () =>
                  navigate(
                    `/products/subscriptions/edit/${record.id}?activeTab=salesAndInsights`
                  ),
                style: {cursor: "pointer"},
              })}
            />
            <div
              style={{
                display: "flex",
                gap: "13px",
                alignItems: "center",
              }}
            >
              <Button
                size="small"
                disabled={!hasPrevPage}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span style={{fontSize: 12, color: "rgba(28, 23, 38, 0.65)"}}>
                Page {page}
              </span>
              <Button
                size="small"
                disabled={!hasNextPage}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </Space>
    </div>
  );
}
